import { useCallback, useState } from "react";

import { DrugDiseaseInteractionApi } from "data";
import { DrugDiseaseInteraction, DrugDiseaseInteractionResult } from "domain/entities/drug-disease-interaction";
import GetAllUseCase from "domain/use-cases/drug-disease-interaction/GetAllUseCase";
import CreateUseCase from "domain/use-cases/drug-disease-interaction/CreateUseCase";
import CreateBatchUseCase from "domain/use-cases/drug-disease-interaction/CreateBatchUseCase";
import UpdateUseCase from "domain/use-cases/drug-disease-interaction/UpdateUseCase";
import DeleteUseCase from "domain/use-cases/drug-disease-interaction/DeleteUseCase";
import { useToken } from "presentation/view-models/hooks";
import { TokenPromise } from "presentation/utils/interfaces/types";
import { IRequestResult2, RequestType } from "presentation/utils/interfaces/IRequestResult2";

// Retorna una promesa para obtener de la API todas las interacciones drogas-enfermedad registrados.
const _getAllDrugDiseaseInteractions = (token: TokenPromise) => {
    const interactionRepository = new DrugDiseaseInteractionApi();
    const getAllUseCase = new GetAllUseCase(interactionRepository);

    return getAllUseCase.getAllDrugDiseaseInteractions(token);
};

// Retorna una promesa para agregar una interaction droga-enfermedad desde la API.
const _createDrugDiseaseInteraction = (interaction: DrugDiseaseInteraction, token: TokenPromise) => {
    const interactionRepository = new DrugDiseaseInteractionApi();
    const createUseCase = new CreateUseCase(interactionRepository);

    return createUseCase.createDrugDiseaseInteraction(interaction, token);
};

// Retorna una promesa para agregar un lote de interactiones droga-enfermedad desde la API.
const _createDrugDiseaseInteractionBatch = (interactions: DrugDiseaseInteraction[], token: TokenPromise) => {
    const interactionRepository = new DrugDiseaseInteractionApi();
    const createBatchUseCase = new CreateBatchUseCase(interactionRepository);

    return createBatchUseCase.createDrugDiseaseInteractionBatch(interactions, token);
};

// Retorna una promesa para editar una interacción droga-enfermedad desde la API.
const _editDrugDiseaseInteraction = (interactionId: number, interaction: DrugDiseaseInteraction, token: TokenPromise) => {
    const interactionRepository = new DrugDiseaseInteractionApi();
    const updateUseCase = new UpdateUseCase(interactionRepository);

    return updateUseCase.updateDrugDiseaseInteraction(interactionId, interaction, token);
};

// Retorna una promesa para eliminar desde la API una interacción droga-enfermedad por su ID.
const _deleteDrugDiseaseInteraction = (interactionId: number, token: TokenPromise) => {
    const interactionRepository = new DrugDiseaseInteractionApi();
    const deleteUseCase = new DeleteUseCase(interactionRepository);

    return deleteUseCase.deleteDrugDiseaseInteraction(interactionId, token);
};

// Hook usado para gestionar el CRUD de interacciones droga-enfermedad.
export const useDrugDiseaseInteractions = () => {
    // Estado del hook.
    const [state, setState] = useState<IRequestResult2>({
        dataLoading: false,
        loading: false,
        reqType: undefined,
        reqCompleted: undefined,
        error: undefined,
    });
    const [data, setData] = useState<DrugDiseaseInteractionResult[]>([]);
    const { getTokens } = useToken();

    const isUpdateStateOk = useCallback((error?: Error | null): boolean => {
        if (error) {
            setState((prev) => ({ ...prev, loading: false, reqCompleted: true, error: error.message }));
            return false;
        } else {
            setState((prev) => ({ ...prev, loading: false, reqCompleted: true, error: undefined }));
            return true;
        }
    }, []);

    const checkExists = useCallback(async (interaction: DrugDiseaseInteraction) => {
        const druID = interaction.drugId ?? null;
        const disID = interaction.diseaseId ?? null;
        const exists = data.find(i => i.drugId === druID && i.diseaseId === disID) !== undefined;
        await new Promise(r => setTimeout(r, 1000));
        if (exists) {
            isUpdateStateOk(new Error("Combinaciones de droga ya existe"));
            return true;
        } else {
            return false;
        }
    }, [data, isUpdateStateOk]);

    // Obtiene de la API todas las interacciones droga-enfermedad registradas.
    const getAllDrugDiseaseInteractions = useCallback(() => {
        setState({ dataLoading: true, loading: false});
        _getAllDrugDiseaseInteractions(getTokens()).then(([res, error]) => {
            setState({
                reqType: RequestType.GET,
                dataLoading: false,
                loading: false,
                error: error?.message,
            });
            setData(res ?? []);
        });
    }, [getTokens]);

    // Guarda una interacción droga-enfermedad desde API
    const saveDrugDiseaseInteraction = useCallback((interaction: DrugDiseaseInteractionResult) => {
        setState((prev) => ({ ...prev, reqType: RequestType.POST, loading: true, reqCompleted: false }));
        checkExists(interaction).then((exists) => {
            if (!exists) {
                _createDrugDiseaseInteraction(interaction, getTokens()).then(([res, error]) => {
                    if (isUpdateStateOk(error)) {
                        interaction.id = res?.data?.id ?? 0;
                        setData((prev) => ([...prev ?? [], interaction]));
                    }
                });
            }
        });
    }, [checkExists, getTokens, isUpdateStateOk]);

    // Guarda un lote de interacciones droga-enfermedad desde API
    const saveDrugDiseaseInteractionBatch = useCallback((interactions: DrugDiseaseInteractionResult[]) => {
        setState((prev) => ({ ...prev, reqType: RequestType.POST, loading: true, reqCompleted: false }));
        _createDrugDiseaseInteractionBatch(interactions, getTokens()).then(([res, error]) => {
            if (isUpdateStateOk(error)) {
                getAllDrugDiseaseInteractions();
            }
        });
    }, [getAllDrugDiseaseInteractions, getTokens, isUpdateStateOk]);

    // Edita una interacción droga-enfermedad desde API
    const editDrugDiseaseInteraction = useCallback((interaction: DrugDiseaseInteractionResult) => {
        setState((prev) => ({ ...prev, reqType: RequestType.PUT, loading: true, reqCompleted: false }));
        _editDrugDiseaseInteraction(interaction.id, interaction, getTokens()).then(([_, error]) => {
            if (isUpdateStateOk(error)) {
                setData((prev) => (prev?.map(item => item.id === interaction.id ? interaction : item)));
            }
        });
    }, [getTokens, isUpdateStateOk]);

    // Elimina una interacción droga-enfermedad del estado
    const deleteDrugDiseaseInteraction = useCallback((interactionId: number) => {
        setState((prev) => ({ ...prev, reqType: RequestType.DELETE, loading: true, reqCompleted: false }));
        _deleteDrugDiseaseInteraction(interactionId, getTokens()).then(([_, error]) => {
            if (isUpdateStateOk(error)) {
                setData((prev) => (prev?.filter(item => item.id !== interactionId)));
            }
        });
    }, [getTokens, isUpdateStateOk]);

    return { getAllDrugDiseaseInteractions, saveDrugDiseaseInteraction, saveDrugDiseaseInteractionBatch, editDrugDiseaseInteraction, deleteDrugDiseaseInteraction, state, data };
};